import { FloatingFormItem, ZipFormField } from '@/components/_common';
import {
  InvoiceCarrierAddress,
  InvoiceCarrierPersonType,
} from '@/core/domain/invoice/invoiceCarrier/invoiceCarrier.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import useAddressQueries from '@/hooks/useAddressQuery';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import validateCpf from '@/utils/functions/validateCpf';
import { applyMask, maskFieldValidator } from '@/utils/mask';
import { cepMask, cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import { removeMaskFromEvent } from '@/utils/mask/removeMask';
import { Col, Form, Input, Row, Select } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { Rule } from 'antd/lib/form';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import MaskedInput from 'react-text-mask';

interface Props {
  form: FormInstance;
  loading?: boolean;
  address?: InvoiceCarrierAddress;
}

export default function InvoiceTransportForm({
  form,
  loading,
  address,
}: Props) {
  const [stateValue, setStateValue] = useState();
  const [disabled, setDisable] = useState(true);
  const addressZip = Form.useWatch(['address', 'zip'], form);
  const defaultFieldError = useDefaultEmptyFieldError();

  const { city, state, zipCode } = useAddressQueries();
  const isLoading = !!loading || zipCode.isFetching;

  useEffect(() => {
    if (zipCode.data && !isEmpty(zipCode.data)) {
      state.setSelectedStateCode(zipCode.data.state);

      setDisable(false);

      form.setFieldsValue({
        address: {
          cityId: zipCode.data.cityId,
          street: zipCode.data.street,
          cityName: zipCode.data.cityName,
          district: zipCode.data.district,
          state: zipCode.data.state,
        },
      });
    }
  }, [zipCode.data]);

  useEffect(() => {
    if (addressZip && addressZip.length < 8) {
      setDisable(true);

      form.setFieldsValue({
        address: {
          cityId: undefined,
          street: undefined,
          city: undefined,
          district: undefined,
          state: undefined,
          number: undefined,
          complement: undefined,
        },
      });
    } else if (addressZip !== applyMask(address?.zip, cepMask)) {
      zipCode.setSelectedZipCode(addressZip);
    } else {
      setDisable(false);
    }
  }, [addressZip]);

  useEffect(() => {
    form.setFieldsValue({ address: { zip: '', cityId: '' } });
    state.setSelectedStateCode(stateValue);
  }, [stateValue]);

  useEffect(() => {
    if (city.selectedCity && address) {
      state.setSelectedStateCode(city.selectedCity.state);

      form.setFieldsValue({
        address: {
          cityId: city.selectedCity.id ?? '',
          state: city.selectedCity.state ?? '',
          cityName: city.selectedCity.name ?? '',
        },
      });
    }
  }, [city.selectedCity, address]);

  useEffect(() => {
    if (address) {
      city.setSelectedCityId(address.cityId);

      form.setFieldsValue({
        address: {
          ...address,
          zip: applyMask(address.zip, cepMask),
        },
      });
    }
  }, [address]);

  const intl = useIntl().formatMessage;
  const translate = (message: string, type: string | 'label' = 'label') =>
    intl({ id: `invoiceTransport.modal.${type}.${message}` });

  const personTypeOptions = [
    {
      value: InvoiceCarrierPersonType.natural,
      label: translate('natural', 'option'),
    },
    {
      value: InvoiceCarrierPersonType.legal,
      label: translate('legal', 'option'),
    },
  ];

  const cpfRules: Rule[] = [
    {
      min: 11,
      max: 11,
      validator: async (rule, value) => {
        if (value) {
          return Promise.all([
            maskFieldValidator(rule, value),
            value.length == rule.max
              ? validateCpf(value)
                ? Promise.resolve()
                : Promise.reject(new Error('CPF inválido'))
              : Promise.resolve(),
          ]);
        }
      },
      message: intl({ id: 'commons.form.errors.invalidCpf' }),
    },
    {
      required: true,
      message: intl({ id: 'commons.form.errors.requiredCpf' }),
    },
  ];

  const cnpjRules: Rule[] = [
    {
      min: 14,
      max: 14,

      validator: async (rule, value) => {
        if (value) {
          return Promise.all([
            maskFieldValidator(rule, value, 'CNPJ'),
            value.length === rule.max
              ? validateCnpj(value)
                ? Promise.resolve()
                : Promise.reject(new Error('CNPJ inválido'))
              : Promise.resolve(),
          ]);
        }
      },

      message: intl({ id: 'commons.form.errors.invalidCnpj' }),
    },
    {
      required: true,
      message: intl({ id: 'commons.form.errors.requiredCnpj' }),
    },
  ];

  const watchPersonType = Form.useWatch('personType', form);

  return (
    <Form layout="vertical" form={form} preserve={false}>
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            name="name"
            label={translate('socialReason')}
            rules={[defaultFieldError]}
          >
            <Input disabled={isLoading} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            name="stateRegistration"
            label={translate('stateRegistration')}
          >
            <Input disabled={isLoading} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            name="personType"
            label={translate('personType')}
            initialValue={InvoiceCarrierPersonType.natural}
          >
            <Select
              options={personTypeOptions}
              disabled={isLoading}
              loading={isLoading}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={
              watchPersonType === InvoiceCarrierPersonType.natural
                ? 'CPF'
                : 'CNPJ'
            }
            name="cpfCnpj"
            getValueFromEvent={(ev) =>
              watchPersonType === InvoiceCarrierPersonType.natural
                ? removeMaskFromEvent(ev)
                : removeMaskFromEvent(ev, 'CNPJ')
            }
            rules={
              watchPersonType === InvoiceCarrierPersonType.natural
                ? cpfRules
                : cnpjRules
            }
          >
            <MaskedInput
              disabled={isLoading}
              className="ant-input"
              mask={
                watchPersonType === InvoiceCarrierPersonType.natural
                  ? cpfMask
                  : cnpjMask
              }
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={7} md={7}>
          <ZipFormField disable={false} fieldName={['address', 'zip']} />
        </Col>

        <Col xs={24} sm={7} md={7}>
          <FloatingFormItem
            name={['address', 'state']}
            label={translate('state')}
          >
            <Select
              onChange={(value) => setStateValue(value)}
              loading={isLoading || state.isFetching}
              options={state?.list.map((state) => ({
                value: state.code,
                label: state.code,
              }))}
              disabled={isLoading}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={10} md={10}>
          <FloatingFormItem
            name={['address', 'cityId']}
            label={translate('city')}
          >
            <Select
              loading={city.isFetching || isLoading}
              options={city?.list.map((city) => ({
                value: city.id,
                label: city.name,
              }))}
              disabled={isLoading}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={18} md={18}>
          <FloatingFormItem
            name={['address', 'street']}
            label={translate('street')}
            rules={[defaultFieldError]}
          >
            <Input disabled={isLoading || disabled} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={6} md={6}>
          <FloatingFormItem
            name={['address', 'number']}
            label={translate('number')}
            rules={[defaultFieldError]}
          >
            <Input disabled={isLoading} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={16} md={16}>
          <FloatingFormItem
            name={['address', 'district']}
            label={translate('district')}
            rules={[defaultFieldError]}
          >
            <Input disabled={isLoading || disabled} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={8} md={8}>
          <FloatingFormItem
            name={['address', 'complement']}
            label={translate('complement')}
          >
            <Input disabled={isLoading} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={24} md={24}>
          <FloatingFormItem name="observation" label={translate('comments')}>
            <Input.TextArea rows={2} disabled={isLoading} />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}
